import * as React from 'react';
import Papa from 'papaparse';
import { isEqual } from 'lodash';

import CSVHelper from './lib/csv';

export type ParsedResults = {
  columnNames: string[];
  parsedRowData: {
    [columnName: string]: string;
  }[];
};

type Props = {
  children: (args: {
    pick: () => void;
    csv?: File;
    parsedResults?: ParsedResults;
    parseErrors?: Papa.ParseError[];
    loading: boolean;
  }) => React.ReactNode;
  numberOfRows?: number;
  // if the parent component needs the parsed results,
  // you can pass in a callback function
  onCSVParse?: (parsedResults: ParsedResults) => void;
  // callback specifically called when parsing is successful
  onSuccess?: () => void;
  // callback specifically called when parsing is unsuccessful
  onError?: () => void;
  csvFromDragDrop?: File;
};

type State = {
  csv?: File;
  parsedResults?: ParsedResults;
  parseErrors?: Papa.ParseError[];
};

/* A CSV parsing component that takes a local CSV file, parses it,
 * and passes any results and errors to a child function
 *
 * <CSVParser>
 *    {({ pick, csv, parsedResults, parseErrors, loading }) => {
 *      if (loading) {
 *        return <CenteredLoadingIndicator />
 *      }
 *      if (!csv) {
 *        return <Button onClick={pick}>Upload a CSV</Button>
 *      }
 *      return (
 *        <RenderingComponent parsedCSV={parsedResults} errors={parseErrors} />
 *      );
 *    }}
 * </CSVParser>
 *
 */

class CSVParser extends React.Component<Props, State> {
  state: State = {};

  constructor(props: Props) {
    super(props);
    this.input = React.createRef();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      !isEqual(prevProps.csvFromDragDrop, this.props.csvFromDragDrop) &&
      this.props.csvFromDragDrop
    ) {
      this.handleCSVPicked({ target: { files: [this.props.csvFromDragDrop] } });
    }
  }

  clearState = () => {
    this.setState({ csv: undefined, parsedResults: undefined, parseErrors: undefined });
  };

  pick = () => {
    if (this.input && this.input.current) {
      this.input.current.click();
    }
  };

  parseCSV = async (csv: File) => {
    const papaParseResults = await CSVHelper.parse(csv);
    const parsedRowData: Array<any> = papaParseResults.data.slice(
      0,
      this.props.numberOfRows || papaParseResults.data.length
    );
    const parsedResults: ParsedResults = {
      columnNames: papaParseResults.meta.fields,
      parsedRowData,
    };
    return { parsedResults, parseErrors: papaParseResults.errors };
  };

  // TS doesn't have good support for `InputEvent`s yet
  // so it complains about `files` not existing on `EventTarget`
  handleCSVPicked = (event: any) => {
    this.clearState();
    const csv: File = event.target.files[0];
    this.setState({ csv }, async () => {
      const { parsedResults, parseErrors } = await this.parseCSV(csv);
      this.setState({ parsedResults, parseErrors });
      if (this.props.onCSVParse) {
        this.props.onCSVParse(parsedResults);
      }
      if (parseErrors.length > 0 && this.props.onError) {
        this.props.onError();
      }
      if (parsedResults.parsedRowData.length > 0 && this.props.onSuccess) {
        this.props.onSuccess();
      }
    });
  };

  input: React.RefObject<HTMLInputElement>;

  render() {
    const { children } = this.props;
    const { csv, parsedResults, parseErrors } = this.state;
    const parsingNotDone = !parsedResults && (!parseErrors || parseErrors.length === 0);
    const loading = Boolean(csv && parsingNotDone);

    return (
      <React.Fragment key="CSV_parser_child">
        <input
          key="input"
          type="file"
          accept=".csv"
          ref={this.input}
          style={{ display: 'none' }}
          onChange={this.handleCSVPicked}
        />
        {children({ pick: this.pick, csv, parsedResults, parseErrors, loading })}
      </React.Fragment>
    );
  }
}

export default CSVParser;
